import React, { useMemo } from 'react';

import { MetricCards } from 'Deer/metricCards/MetricCards';
import { COMPENSATION_METRIC_CARDS, MODULE_KEYS_METRIC_CARDS_DEER } from 'Deer/metricCards/constants';

import PlanningTableData from '../json/PlanningTable.json';

export const checkOutsideGuidelineLimit = (PROPOSED_BASE_SALARY, BASE_RATE) => {
  const proposedSalary = PROPOSED_BASE_SALARY?.value;
  const baseSalary = BASE_RATE?.value;
  const guideline = PROPOSED_BASE_SALARY?.guidanceValue;
  const { minPercentage, maxPercentage } = guideline ?? {};
  const minGuidedSalary = baseSalary * ((100 + minPercentage) / 100);
  const maxGuidedSalary = baseSalary * ((100 + maxPercentage) / 100);

  return proposedSalary > maxGuidedSalary || proposedSalary < minGuidedSalary;
};

export const PlanningMetricCards = ({
  gridStatePersistenceKey,
  activeTab,
}: {
  gridStatePersistenceKey: string;
  activeTab: string;
}) => {
  const { rows } = PlanningTableData;

  const isApproveTab = activeTab === 'approve';
  const isPublishTab = activeTab === 'publish';
  const isProposeTab = activeTab === 'propose';

  const {
    pendingOnProposerCount,
    pendingOnApproverCount,
    pendingOnPublisherCount,
    outsideGuideLineCount,
    outsideGuideLineApprovedCount,
    outsideBudgetCount,
  } = useMemo(() => {
    let pendingOnProposer = 0;
    let pendingOnApprover = 0;
    let pendingOnPublisher = 0;
    let outsideGuideline = 0;
    let outsideGuidelineApproved = 0;
    let outsideBudget = 0;

    rows?.forEach((rowData) => {
      const isSubRole = rowData?.hierarchy?.length > 1;
      if (isSubRole) {
        return;
      }

      // Count by WAITING_ON status
      switch (rowData?.WAITING_ON?.status) {
        case 'IN_PROGRESS':
          pendingOnProposer += 1;
          break;
        case 'AWAITING_APPROVAL':
          pendingOnApprover += 1;
          break;
        case 'REVIEW_PENDING':
          pendingOnPublisher += 1;
          break;
        default:
          break;
      }

      if (isApproveTab) {
        outsideGuidelineApproved += checkOutsideGuidelineLimit(rowData?.APPROVED_PAY, rowData?.BASE_RATE) ? 1 : 0;
      } else {
        outsideGuideline += checkOutsideGuidelineLimit(rowData?.PROPOSED_BASE_SALARY, rowData?.BASE_RATE) ? 1 : 0;
      }

      const proposedPay = rowData?.PROPOSED_RECOMMENDATION?.value;
      const approvedPay = rowData?.APPROVED_PAY?.value;

      if (proposedPay > approvedPay) {
        outsideBudget += 1;
      }
    });

    return {
      pendingOnProposerCount: pendingOnProposer,
      pendingOnApproverCount: pendingOnApprover,
      pendingOnPublisherCount: pendingOnPublisher,
      outsideGuideLineCount: outsideGuideline,
      outsideGuideLineApprovedCount: outsideGuidelineApproved,
      outsideBudgetCount: outsideBudget,
    };
  }, [rows]);

  const metrics = [
    {
      cardId: COMPENSATION_METRIC_CARDS.pendingOnProposer,
      title: 'Pending on proposer',
      value: activeTab === 'publish' ? 28 : pendingOnProposerCount,
    },
    {
      cardId: COMPENSATION_METRIC_CARDS.pendingOnApprover,
      title: 'Pending on approver',
      value: pendingOnApproverCount,
    },
    {
      cardId: COMPENSATION_METRIC_CARDS.pendingOnPublisher,
      title: 'Pending on publisher',
      value: pendingOnPublisherCount,
    },
    ...(isApproveTab
      ? [
          {
            cardId: COMPENSATION_METRIC_CARDS.outsideGuidelineApproved,
            title: 'Outside guidelines',
            value: outsideGuideLineApprovedCount,
          },
        ]
      : [
          {
            cardId: COMPENSATION_METRIC_CARDS.outsideGuideline,
            title: 'Outside guidelines',
            value: outsideGuideLineCount,
          },
        ]),
    ...(isApproveTab || isPublishTab
      ? [
          {
            cardId: COMPENSATION_METRIC_CARDS.outsideBudget,
            title: 'Outside budget',
            value: outsideBudgetCount,
          },
        ]
      : []),
  ];

  return (
    <MetricCards
      height="100%"
      columns={`repeat(${metrics.length}, 1fr)`}
      cards={metrics}
      module={MODULE_KEYS_METRIC_CARDS_DEER.COMPENSATION}
      gridStatePersistenceKey={gridStatePersistenceKey}
      cardTitle="Metrics"
      cardStyle="style1"
    />
  );
};
